import React, {
    ReactElement, useEffect, useState,
} from "react";
import { IdentityProvider, useLoginMutation, useLoginWithProviderLazyQuery } from "../../../__generated__/api";
import TimedMessage from "../../../components/timed-message/TimedMessage";
import usePortal from "../../../contexts/PortalContext";
import useRouting from "../../../contexts/Routing";
import useCurrentSession from "../../../contexts/UserContext";
import "./Login.css";
import { HtmlRenderer } from "../../../components/html-render/HtmlRenderer";

export default function Login(): ReactElement {
    const { invalidate, clearCache } = useCurrentSession();
    const { clearCache: clearPortalCache, title, welcomeMessage } = usePortal();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState<string | null>();
    const [loginHandler] = useLoginMutation();
    const [loginWithProvider] = useLoginWithProviderLazyQuery();

    const {
        historyPush, Paths, getSearchParams, historyPushSearch,
    } = useRouting();

    const searchParams = getSearchParams(Paths.LOGIN);
    const { error: ssoLoginError, redirect } = searchParams;

    async function login() {
        try {
            if (!email || !password) {
                setLoginError("Please enter your email and password.");
                return;
            }
            const { data, errors } = await loginHandler({ variables: { email, password } });

            if (errors) {
                setLoginError("Error while logging in");
                return;
            }
            if (data?.login.__typename === "LoginError") {
                setLoginError(data?.login.message);
                return;
            }
            invalidate();

            historyPush(Paths.HOME);
        } catch (err) {
            setLoginError("Error while trying to login.");
            invalidate();
        }
    }

    useEffect(() => {
        if (loginError) {
            setLoginError(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, password]);

    function handleSubmit(evt: React.FormEvent<HTMLFormElement>) {
        evt.preventDefault();
        login();
    }

    const loginProvider = async (provider: IdentityProvider) => {
        const results = await loginWithProvider({
            variables: {
                provider: {
                    provider,
                    redirectUrl: redirect ?? `https://${window.location.host}`,
                },
            },
        });

        const url = results.data?.loginWithIdentityProvider.url;
        if (!url) {
            return;
        }
        clearCache();
        clearPortalCache();
        window.location.href = url;
    };

    return (
        <div className="full-page">

            <TimedMessage
                shouldShow={ssoLoginError != null && ssoLoginError !== ""}
                title="Error loging in"
                variant="danger"
                onClose={() => {
                    delete (searchParams as Partial<typeof searchParams>).error;
                    historyPushSearch(searchParams);
                }}
            >
                {ssoLoginError}
            </TimedMessage>

            <div className="centered-container pt-5 flex-row align-items-start">
                <div className="w-50 border-end pe-3 me-3 d-none d-lg-block ">
                    <h5>
                        Welcome to
                        {" "}
                        {title}
                    </h5>
                    <div className="mt-3">
                        {welcomeMessage && (
                            <HtmlRenderer html={welcomeMessage} />
                        )}
                        <small>
                            To sign this portal, please choose to sign in with Slack or Google.
                            You can also sign in directly from the Slack application.
                            {" "}
                            <a
                                href="https://support.foqal.io/answers/urn:fq:article:7ca9e99f6ebc4755acd94052616e1e84"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Read more
                            </a>
                            {" "}
                            to find out how.
                        </small>

                    </div>

                </div>
                <div className="flex-grow-1 flex-shrink-1 ">
                    <div className="d-flex justify-content-center flex-column col-6 mx-auto">
                        <button
                            type="button"
                            className="btn btn-outline d-flex align-items-center p-0 pe-3 login-provider-slack bg-white"
                            onClick={() => loginProvider(IdentityProvider.Slack)}
                        >
                            <img src="https://static.foqal.io/library/slack.svg" alt="Slack logo" className="me-2" />
                            <div>
                                Sign in with Slack
                            </div>
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline d-flex align-items-center p-0 pe-3 login-provider-google mt-3 bg-white"
                            onClick={() => loginProvider(IdentityProvider.Google)}
                        >
                            <img
                                src="https://static.foqal.io/library/google-signin.svg"
                                alt="Google logo"
                                className=" me-2"
                            />
                            <div>
                                Sign in with Google
                            </div>
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
}
